import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Loader2, PlusCircle, PackageCheck } from "lucide-react";

// MG Rugs yarn ledger — log Royal Indian Carpets purchases per weaver + dye lot,
// see per-weaver summary (purchased vs. estimated std, variance %).
export default function MGYarnLedger() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "hi" ? hi : en);
  const [weavers, setWeavers] = useState([]);
  const [pos, setPos] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({
    weaver_id: "", weaver_po_id: "", colour: "",
    dye_lot: "", kg: "", purchase_date: new Date().toISOString().slice(0, 10),
    supplier: "Royal Indian Carpets Mfg. Co. Pvt. Ltd.",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [w, p, pr, s] = await Promise.all([
        api.get("/mg/weavers").catch(() => ({ data: [] })),
        api.get("/mg/weaver-pos").catch(() => ({ data: [] })),
        api.get("/mg/yarn/purchases"),
        api.get("/mg/yarn/weaver-summary"),
      ]);
      setWeavers(w.data || []);
      setPos(p.data || []);
      setPurchases(pr.data || []);
      setSummary(s.data || []);
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const weaverName = (id) => weavers.find((w) => w.id === id)?.name || id?.slice(0, 8) || "—";
  const posForWeaver = (wid) => pos.filter((p) => p.weaver_id === wid);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    if (!form.weaver_id) return setMsg(t("Select weaver", "बुनकर चुनें"));
    if (!form.dye_lot.trim()) return setMsg(t("Dye lot required", "डाई लॉट ज़रूरी"));
    if (!parseFloat(form.kg)) return setMsg(t("Enter kg", "किलो दर्ज करें"));
    setBusy(true);
    try {
      await api.post("/mg/yarn/purchases", {
        ...form,
        kg: parseFloat(form.kg),
      });
      setForm((f) => ({ ...f, weaver_po_id: "", colour: "", dye_lot: "", kg: "" }));
      setMsg(t("Purchase logged", "खरीद दर्ज हुई"));
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const varianceChip = (v, flag) => {
    if (flag) return <span className="status-chip pace-red">{v}%</span>;
    if (v > 0) return <span className="status-chip pace-amber">+{v}%</span>;
    return <span className="status-chip pace-green">{v}%</span>;
  };

  return (
    <div data-testid="mg-yarn">
      <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        {t("Yarn Ledger", "यार्न लेजर")}
      </h1>
      <p className="text-xs text-[#7C8E9E] mb-4">
        {t("Operational tracker for MG Rugs — Royal Indian Carpets purchases, dye lots & consumption by weaver. HAG uses this as informational context only; it does NOT feed HAG stock or Tally.",
           "MG रग्स संचालन — Royal Indian Carpets खरीद, डाई लॉट व बुनकर वार खपत। HAG के स्टॉक/Tally से लिंक नहीं।")}
      </p>

      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="mg-yarn-msg">{msg}</div>}

      <form onSubmit={submit} className="hag-surface p-4 mb-6" data-testid="mg-yarn-form">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: "var(--brand-navy)" }}>
          <PlusCircle size={16} /> {t("Log a new purchase", "नई खरीद दर्ज करें")}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Weaver", "बुनकर")}</label>
            <select data-testid="mg-yarn-weaver" value={form.weaver_id}
              onChange={(e) => setForm({ ...form, weaver_id: e.target.value, weaver_po_id: "" })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">— {t("select", "चुनें")} —</option>
              {weavers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Weaver PO (opt.)", "बुनकर PO (वैकल्पिक)")}</label>
            <select data-testid="mg-yarn-po" value={form.weaver_po_id}
              onChange={(e) => setForm({ ...form, weaver_po_id: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm bg-white">
              <option value="">—</option>
              {posForWeaver(form.weaver_id).map((p) => <option key={p.id} value={p.id}>{p.po_number}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Dye lot", "डाई लॉट")}</label>
            <input data-testid="mg-yarn-lot" value={form.dye_lot}
              onChange={(e) => setForm({ ...form, dye_lot: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" placeholder="RIC-2026-A17" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Colour", "रंग")}</label>
            <input data-testid="mg-yarn-colour" value={form.colour}
              onChange={(e) => setForm({ ...form, colour: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Kg", "किलो")}</label>
            <input data-testid="mg-yarn-kg" type="number" step="0.1" min="0" value={form.kg}
              onChange={(e) => setForm({ ...form, kg: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Purchase date", "खरीद तिथि")}</label>
            <input data-testid="mg-yarn-date" type="date" value={form.purchase_date}
              onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Supplier", "आपूर्तिकर्ता")}</label>
            <input data-testid="mg-yarn-supplier" value={form.supplier}
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              className="w-full h-11 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
        </div>
        <div className="mt-3">
          <button type="submit" data-testid="mg-yarn-submit" disabled={busy}
            className="h-11 px-4 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ background: "var(--brand-navy)", color: "white" }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PackageCheck size={14} />}
            {t("Log purchase", "खरीद दर्ज करें")}
          </button>
        </div>
      </form>

      <h2 className="text-sm font-bold uppercase tracking-widest text-[#4A627A] mb-2">{t("Weaver-wise summary", "बुनकर वार सारांश")}</h2>
      {loading ? <div className="text-sm text-[#7C8E9E]">Loading…</div> : (
        <div className="hag-surface overflow-x-auto mb-6" data-testid="mg-yarn-summary-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
                <th className="text-left px-3 py-2">{t("Weaver", "बुनकर")}</th>
                <th className="text-right px-3 py-2">{t("Purchased kg", "खरीद कि.")}</th>
                <th className="text-right px-3 py-2">{t("Consumed (closed POs) kg", "खपत (बंद PO) कि.")}</th>
                <th className="text-right px-3 py-2">{t("Est. std kg", "अनुमानित कि.")}</th>
                <th className="text-right px-3 py-2">{t("Variance", "अंतर")}</th>
                <th className="text-left px-3 py-2">{t("Dye lots", "डाई लॉट")}</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((r) => (
                <tr key={r.weaver_id} className="border-t border-[#1B3A5C]/10" data-testid={`mg-yarn-summary-${r.weaver_id}`}>
                  <td className="px-3 py-2 font-semibold">{weaverName(r.weaver_id)}</td>
                  <td className="px-3 py-2 text-right">{r.total_purchased_kg}</td>
                  <td className="px-3 py-2 text-right">{r.consumed_on_closed_pos_kg}</td>
                  <td className="px-3 py-2 text-right">{r.estimated_std_kg}</td>
                  <td className="px-3 py-2 text-right">{varianceChip(r.variance_pct, r.variance_flag)}</td>
                  <td className="px-3 py-2 text-xs text-[#4A627A]">{(r.dye_lots || []).join(", ") || "—"}</td>
                </tr>
              ))}
              {summary.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-[#7C8E9E]">{t("No purchases logged yet.", "अभी कोई खरीद नहीं।")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-sm font-bold uppercase tracking-widest text-[#4A627A] mb-2">{t("Recent purchases", "हाल की खरीद")}</h2>
      <div className="hag-surface overflow-x-auto" data-testid="mg-yarn-purchases-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
              <th className="text-left px-3 py-2">{t("Date", "तिथि")}</th>
              <th className="text-left px-3 py-2">{t("Weaver", "बुनकर")}</th>
              <th className="text-left px-3 py-2">{t("Dye lot", "डाई लॉट")}</th>
              <th className="text-left px-3 py-2">{t("Colour", "रंग")}</th>
              <th className="text-right px-3 py-2">{t("Kg", "कि.")}</th>
              <th className="text-left px-3 py-2">{t("Supplier", "आपूर्तिकर्ता")}</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p) => (
              <tr key={p.id} className="border-t border-[#1B3A5C]/10" data-testid={`mg-yarn-purchase-${p.id}`}>
                <td className="px-3 py-2 text-xs">{new Date(p.purchase_date).toLocaleDateString()}</td>
                <td className="px-3 py-2">{weaverName(p.weaver_id)}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.dye_lot}</td>
                <td className="px-3 py-2 text-xs">{p.colour || "—"}</td>
                <td className="px-3 py-2 text-right">{p.kg}</td>
                <td className="px-3 py-2 text-xs">{p.supplier}</td>
              </tr>
            ))}
            {purchases.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-[#7C8E9E]">{t("No purchases yet.", "अभी कोई खरीद नहीं।")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
